import axios from 'axios'
import { API_BASE_URL } from '@/config/apiBaseUrl'
import { redactSecretText } from '@/utils/redactSecrets'
import { apiErrorMessageFor } from '@/utils/apiErrors'

const SECRET_KEYS = new Set([
  'authorization', 'proxyauthorization', 'cookie', 'setcookie',
  'token', 'authtoken', 'accesstoken', 'refreshtoken', 'idtoken', 'updatertoken',
  'password', 'passwd', 'pass', 'pwd', 'psw', 'secret', 'key', 'auth',
  'apikey', 'authkey', 'privatekey', 'streamkey', 'credential', 'credentials'
])

function isSecretKey(key: string): boolean {
  const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, '')
  return SECRET_KEYS.has(normalized)
    || normalized.endsWith('token')
    || normalized.endsWith('password')
    || normalized.endsWith('secret')
    || normalized.endsWith('apikey')
    || normalized.endsWith('authkey')
    || normalized.endsWith('privatekey')
}

function sanitizeResponseData(value: unknown, depth = 0): unknown {
  if (depth > 8) return '[REDACTED_DEPTH]'
  if (typeof value === 'string') return redactSecretText(value)
  if (Array.isArray(value)) return value.map(item => sanitizeResponseData(item, depth + 1))
  if (!value || typeof value !== 'object') return value

  return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([key, item]) => [
    key,
    isSecretKey(key)
      ? '[REDACTED]'
      : sanitizeResponseData(item, depth + 1)
  ]))
}

/**
 * Axios errors retain the complete request config, including Authorization.
 * Reject only a small public projection so component console calls and error
 * reporters cannot serialize the admin bearer.
 */
export function sanitizeApiError(error: any): Error & {
  code?: string
  status?: number
  response?: { status?: number; data?: unknown }
} {
  // Ответы preHandler'ов приходят как {error, code} без message — тогда вместо
  // «Request failed with status code 403» берём русский текст по code/статусу.
  const responseData = error?.response?.data
  const serverMessage = typeof responseData?.message === 'string' && responseData.message.trim()
    ? responseData.message
    : null
  const fallbackMessage = typeof error?.response?.status === 'number'
    ? apiErrorMessageFor({
        code: typeof responseData?.code === 'string' ? responseData.code : null,
        status: error.response.status,
        details: responseData?.details
      })
    : null
  const publicMessage = redactSecretText(
    serverMessage || fallbackMessage || error?.message || 'Request failed'
  )
  const safeError = new Error(publicMessage || 'Request failed') as Error & {
    code?: string
    status?: number
    response?: { status?: number; data?: unknown }
  }
  safeError.name = 'ApiError'
  if (typeof error?.code === 'string') safeError.code = error.code
  if (typeof error?.response?.status === 'number') {
    safeError.status = error.response.status
    safeError.response = {
      status: error.response.status,
      data: sanitizeResponseData(error.response.data)
    }
  }
  return safeError
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

function isAdminTokenRequest(config: { headers?: { get?: (name: string) => unknown; Authorization?: unknown } } | undefined): boolean {
  const authorization = config?.headers?.get?.('Authorization')
    ?? config?.headers?.Authorization
  const adminToken = localStorage.getItem('admin_token')
  return !authorization || authorization === `Bearer ${adminToken}`
}

// Публичные championship GET-endpoint'ы используют optional auth: анонимам
// показывают только опубликованные данные, администратору — draft/pending.
// Раньше admin_token добавлялся вручную только к части POST/PUT-запросов,
// поэтому GET списка, edition monitor и format builder всегда выглядели
// анонимными. Добавляем токен ко всем запросам этого API-клиента, если он есть.
api.interceptors.request.use(config => {
  const token = localStorage.getItem('admin_token')
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(
  response => {
    // Championship и часть других read-роутов намеренно публичны: сервер не
    // возвращает 401 для плохого Bearer, а отвечает как anonymous. Если у
    // админки был сохранён токен, такой ответ нельзя принимать за пустую
    // очередь/отсутствие данных — очищаем сессию и переводим на login.
    const optionalAuthState = response.headers?.['x-igo-optional-auth-state']
    if (optionalAuthState === 'invalid'
      && localStorage.getItem('admin_token')
      && isAdminTokenRequest(response.config)) {
      localStorage.removeItem('admin_token')
      window.location.href = '/login'
      return Promise.reject(new Error('Admin session has expired'))
    }
    return response
  },
  error => {
    if (error.response?.status === 401) {
      if (isAdminTokenRequest(error.config)) {
        localStorage.removeItem('admin_token')
        window.location.href = '/login'
      }
    }
    return Promise.reject(sanitizeApiError(error))
  }
)

export default api
